import React, { useState } from 'react';
import Image from 'next/image';
import Router, { useRouter } from 'next/router';
import { Container, Row, Col } from 'react-bootstrap';
import { selectCurrentUser } from '@/stores/currentUserApi';
import { selectCartTotal } from '@/stores/cartApi';
import { useAppSelector, type AppRootState } from '@/configureStore';
import { ShoppingCartButton } from '@/components/ShoppingCartButton';
import { ConnectedSearchBox } from '@/components/Search/Connectors/ConnectedSearchBox';
import { useSafeTemplateData } from '@/hooks/useSafeTemplateData';
import { useShoppingCartRoute } from '@/services/feature-toggles-service';
import { ToggleTax } from './Components/ToggleTax';
import { UserArea } from './Components/UserArea';
import { MainNavigation } from './Components/MainNavigation';
import { ToggleMenuButton } from './Components/ToggleMenuButton';
import { SectorNavigation } from './Components/SectorNavigation';
import styles from './Header.module.scss';
import { INavigationItem } from './HeaderTypes';
import { CustomerRating } from './CustomerRating/CustomerRating';
import type * as Types from '@/pages/mijn-carel-lurvink/catalogus/stores/types';

export interface IHeader {
  categories: Types.Category[];
}

export const Header = ({ categories }: IHeader) => {
  const cartCount = useAppSelector((state: AppRootState) => selectCartTotal(state));
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const router = useRouter();
  const templateData = useSafeTemplateData();
  const currentUser = useAppSelector((state: AppRootState) => selectCurrentUser(state));
  const shoppingCartRoute = useShoppingCartRoute();

  // Early return with loading state if templateData is not available
  if (!templateData) {
    return (
      <header className={`${styles.header} ${styles.fullHeader}`}>
        <Container>
          <Row>
            <Col>Loading...</Col>
          </Row>
        </Container>
      </header>
    );
  }

  const mainNavigationItems = templateData.sector.navigation.items.map(
    (item: ApiLink, index: number): INavigationItem => ({
      text: item.text ?? '',
      href: item.href,
      id: `nav-${index}`,
      target: item.target || undefined
    })
  );

  const closeMenu = () => setIsMenuOpen(false);
  Router.events.on('routeChangeComplete', closeMenu);
  Router.events.on('routeChangeError', closeMenu);

  const shouldDisplayCategories = true; //!currentUser?.isBusinessAccount ?? true;

  return (
    <header className={`${styles.header} ${styles.fullHeader}`}>
      <Container>
        <Row className="mb-2">
          <Col>
            <SectorNavigation templateData={templateData} />
          </Col>
          <Col xs={{ span: 'auto' }} className="ml-auto">
            <ToggleTax />
          </Col>
        </Row>
        <Row className="align-items-center">
          <Col>
            <div style={{ height: '1rem' }}></div>
          </Col>
        </Row>
        <Row className="align-items-center justify-content-between">
          <Col xs="auto" className="d-lg-none">
            <ToggleMenuButton onClick={() => setIsMenuOpen(!isMenuOpen)} isMenuOpen={isMenuOpen} />
          </Col>
          <Col xs={{ order: 2, span: 'auto' }} lg={{ span: 3, order: 1 }} className="mb-0">
            <a href="/">
              <Image src={templateData.sector.logo} alt="Carel Lurvink logo" width={60} height={50} className={styles.logo} priority />
            </a>
          </Col>
          <Col xs={{ span: 12, order: 12 }} lg={{ span: 6, order: 3 }}>
            <ConnectedSearchBox user={currentUser} />
          </Col>
          <Col xs={{ span: 'auto', order: 5 }} lg={{ order: 3, span: 'auto' }} className="d-none d-lg-block align-self-center ml-auto">
            <UserArea />
          </Col>
          <Col xs={{ order: 6, span: 'auto' }} lg={{ order: 5, span: 'auto' }} className="d-flex justify-content-end">
            <ShoppingCartButton cartCount={cartCount} size="lg" onClick={() => router.push(shoppingCartRoute)} />
          </Col>
        </Row>

        <Row>
          <Col id="navigation">
            <MainNavigation
              isOpen={isMenuOpen}
              navigationItems={mainNavigationItems}
              onClose={closeMenu}
              trueFullScreen={true}
              user={currentUser}
              {...(shouldDisplayCategories && { categories })}
            />
          </Col>
        </Row>
        <Row className="py-3 d-none d-xl-flex">
          <Col>
            <div className={styles.headerUsps}>
              <ul>
                {templateData.usps.items.map((item, idx) => (
                  <li key={idx}>
                    <a href={item.href} className={item.class}>
                      {item.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </Col>
          <Col>
            <CustomerRating data={templateData} />
          </Col>
        </Row>
      </Container>
    </header>
  );
};
